import { useState, useEffect, useContext } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Breadcrumbs, Typography, Box, FormControl, Paper, TextField, Select, MenuItem, InputLabel, FormHelperText, OutlinedInput, InputAdornment, IconButton } from '@mui/material';
import dayjs from '../../../../config/dayjs';

import { useToaster } from "../../../../providers/ToasterProvider";
import { enableExam } from '../../../../services/masterService';
import { getDataForEnableExam } from '../../../../services/masterService';
import { MainContainer, Container } from '../../../../components';
import { Menu } from '../Menu/Menu';

import ARROW from '../../../../assets/images/breadcrumb-arrow.svg';
import ARROW_OPEN from '../../../../assets/images/breadcrumb-arrow-open.svg';
import EYE_ICON from '../../../../assets/images/eye.svg';
import EYE_CLOSE_ICON from '../../../../assets/images/eye-close.svg'

import { LoadingButton } from '@mui/lab';
import { ProjectContext } from '../../../../providers/projectProvider';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

export const EnableExam = () => {

    const params = useParams();
    const projectName = params?.projectName;
    const location = useLocation();
    const navigate = useNavigate();
    const { addNotification } = useToaster();
    const { projectDetails } = useContext(ProjectContext);
    const [configuration, setConfiguration] = useState({
        driveName: '',
        courseName: '',
        batchName: '',
        examDate: '',
    });
    const [projectMasterId, setProjectMasterId] = useState(null);
    const [assignedSets, setAssignedSets] = useState([]);
    const [availableSets, setAvailableSets] = useState([]);
    const [selectedSet, setSelectedSet] = useState(0);
    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);
    const [isEnabling, setIsEnabling] = useState(false);
    const [startExamBetween, setStartExamBetween] = useState(false);
    const [timeDuration, setTimeDuration] = useState(0);
    const [unlockWithPassword, setUnlockWithPassword] = useState(false);

    const [passwordVisible, setPasswordVisible] = useState(false);
    const setSchema = z.object({
        set: z.number().min(1, { message: 'Set is required.' }),
        password: z.string().optional(),
    }).superRefine(({ password }, ctx) => {
        if (unlockWithPassword && !password) {
            ctx.addIssue({
                code: 'custom',
                message: 'Password is required.',
                path: ['password']
            })
        }
        if (unlockWithPassword && password && password.length !== 6) {
            ctx.addIssue({
                code: 'custom',
                message: 'Password must be 6 characters long.',
                path: ['password']
            });
        }
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        trigger,
        setValue,
    } = useForm({
        resolver: zodResolver(setSchema),
    });

    useEffect(() => {

        const projectMasterId = location?.state?.projectMasterId;
        const driveName = location?.state?.driveName;
        const courseName = location?.state?.courseName;
        const batchName = location?.state?.batchName;
        const examDate = location?.state?.examDate;
        if (projectMasterId && driveName && courseName && batchName && examDate) {
            setConfiguration({
                driveName,
                courseName,
                batchName,
                examDate
            });
            setProjectMasterId(projectMasterId);
            const fetchData = async () => {
                try {
                    const res = await getDataForEnableExam(projectMasterId);
                    if (res.code === 'OK') {
                        setAssignedSets(res.data.assignedSets);
                        setStartTime(dayjs.utc(res.data.startTime));
                        setEndTime(dayjs.utc(res.data.endTime));
                        setStartExamBetween(res.data.startExamBetweenSpecifiedTime);
                        setTimeDuration(res.data.timeDuration);
                        setAvailableSets(Array.from({ length: res.data.numberOfSets }, (_, i) => i + 1))
                        setUnlockWithPassword(res.data.unlockWithPassword);
                    }
                } catch (error) {
                    console.log('There is an error while getting data for enable exam', error);
                }
            }
            fetchData();
        } else {
            if (projectName) {
                navigate(`/${projectName}/content-dashboard`)
            }
        }
    }, [location]);

    const handleEnableExam = async (data) => {
        if (projectMasterId && data.set) {
            if (unlockWithPassword && !data.password) {
                setIsEnabling(false);
                return;
            }
            setIsEnabling(true);
            try {
                const res = await enableExam(projectMasterId, data)
                if (res.code === 'OK') {
                    addNotification('Exam enabled successfully.', 'success');
                    if (projectName) {
                        navigate(`/${projectName}/content-dashboard`);
                    }
                }
            } catch (error) {
                const code = error?.response?.data?.code;
                if (code === 'E_EXAM_ALREADY_ENABLED') {
                    addNotification('Exam already enabled.', 'danger');
                } else if (code === 'CANDIDATES_NOT_FOUND') {
                    addNotification('Candidates not found.', 'danger');
                } else if (code === 'INSTRUCTIONS_NOT_UPLOADED') {
                    addNotification('Instructions not uploaded.', 'danger');
                } else if (code === 'FEEDBACK_NOT_UPLOADED') {
                    addNotification('Feedback not uploaded.', 'danger');
                } else if (code === 'CONFIGURATION_NOT_PROPERLY_CONFIGURED') {
                    addNotification('Configuration does not configured properly.', 'danger');
                } else if (code === 'EXAM_START_TIME_EXPIRED') {
                    addNotification('Exam start time expired can not enable exam.', 'danger');
                }
                console.log('There is an error while enabling exam', error);
            } finally {
                setIsEnabling(false);
            }
        }
    }

    return (
        <MainContainer>
            <Menu>
                <Breadcrumbs aria-label="breadcrumb" separator='' sx={{ gap: 12 }}>
                    <Typography color="text.primary" sx={{
                        fontWeight: 400,
                        textTransform: 'capitalize'
                    }}>{projectDetails?.projectName}</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW} alt=''></img>
                    </Box>

                    <Typography color="text.primary" sx={{ fontWeight: 500 }}>Enable Exam Schedule</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW_OPEN} alt='' ></img>
                    </Box>
                </Breadcrumbs>
            </Menu>

            <Container>
                <div className="search-header-wrapper" sx={{ mb: 1.5 }}>
                    <h1 className='title' style={{ margin: '0px' }}>Enable Exam Schedule</h1>
                </div>

                <Paper sx={{
                    width: '100%',
                    overflow: 'hidden',
                    bgcolor: 'background.base',
                    maxWidth: 'calc(100vw - 140px)',
                    borderRadius: 3,
                    px: 3,
                    py: 5,
                    mb: 4
                }}>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    }}>
                        <Box sx={{
                            display: 'flex',
                            gap: 2,
                            alignItems: 'center'
                        }}>
                            <Typography variant='h6' sx={{ minWidth: 230 }}>Drive</Typography>
                            <Typography variant='h6' sx={{ color: 'primary.main', textTransform: 'capitalize' }}>
                                {configuration?.driveName}
                            </Typography>
                        </Box>
                        <Box sx={{
                            display: 'flex',
                            gap: 2,
                            alignItems: 'center'
                        }}>
                            <Typography variant='h6' sx={{ minWidth: 230 }}>Course</Typography>
                            <Typography variant='h6' sx={{ color: 'primary.main', textTransform: 'capitalize' }}>
                                {configuration?.courseName}
                            </Typography>
                        </Box>
                        <Box sx={{
                            display: 'flex',
                            gap: 2,
                            alignItems: 'center'
                        }}>
                            <Typography variant='h6' sx={{ minWidth: 230 }}>Batch</Typography>
                            <Typography variant='h6' sx={{ color: 'primary.main', textTransform: 'capitalize' }}>
                                {configuration?.batchName}
                            </Typography>
                        </Box>
                        {startExamBetween
                            ? <>
                                <Box sx={{
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: 'center'
                                }}>
                                    <Typography variant='h6' sx={{ minWidth: 230 }}>Exam Start Date</Typography>
                                    <Typography variant='h6' sx={{ color: 'primary.main', textTransform: 'capitalize' }}>
                                        {startTime.format('DD-MM-YYYY')}
                                    </Typography>
                                </Box>
                                <Box sx={{
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: 'center'
                                }}>
                                    <Typography variant='h6' sx={{ minWidth: 230 }}>Exam Start Time</Typography>
                                    <Typography variant='h6' sx={{ color: 'primary.main' }}>
                                        {startTime?.format('hh:mm A')}
                                    </Typography>
                                </Box>
                                <Box sx={{
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: 'center'
                                }}>
                                    <Typography variant='h6' sx={{ minWidth: 230 }}>Exam End Date</Typography>
                                    <Typography variant='h6' sx={{ color: 'primary.main', textTransform: 'capitalize' }}>
                                        {endTime.format('DD-MM-YYYY')}
                                    </Typography>
                                </Box>
                                <Box sx={{
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: 'center'
                                }}>
                                    <Typography variant='h6' sx={{ minWidth: 230 }}>Exam End Time</Typography>
                                    <Typography variant='h6' sx={{ color: 'primary.main' }}>
                                        {endTime?.format('hh:mm A')}
                                    </Typography>
                                </Box>
                                <Box sx={{
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: 'center'
                                }}>
                                    <Typography variant='h6' sx={{ minWidth: 230 }}>Exam Duration (HH:MM)</Typography>
                                    <Typography variant='h6' sx={{ color: 'primary.main' }}>
                                        {timeDuration}
                                    </Typography>
                                </Box>
                            </>
                            : <>
                                <Box sx={{
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: 'center'
                                }}>
                                    <Typography variant='h6' sx={{ minWidth: 230 }}>Exam Date</Typography>
                                    <Typography variant='h6' sx={{ color: 'primary.main', textTransform: 'capitalize' }}>
                                        {configuration?.examDate}
                                    </Typography>
                                </Box>
                                <Box sx={{
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: 'center'
                                }}>
                                    <Typography variant='h6' sx={{ minWidth: 230 }}>Start Time</Typography>
                                    <Typography variant='h6' sx={{ color: 'primary.main' }}>
                                        {startTime?.format('hh:mm A')}
                                    </Typography>
                                </Box>
                                <Box sx={{
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: 'center'
                                }}>
                                    <Typography variant='h6' sx={{ minWidth: 230 }}>End Time</Typography>
                                    <Typography variant='h6' sx={{ color: 'primary.main' }}>
                                        {endTime?.format('hh:mm A')}
                                    </Typography>
                                </Box>
                            </>}
                        <Box sx={{
                            display: 'flex',
                            gap: 2,
                            alignItems: 'center',
                            mt: 3
                        }}>
                            <Box component='form' onSubmit={handleSubmit(handleEnableExam)} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 2 }}>
                                <FormControl sx={{ minWidth: 180, bgcolor: 'background.base' }}>
                                    <InputLabel>Set</InputLabel>
                                    <Select
                                        label="Set"
                                        error={Boolean(errors.set)}
                                        onBlur={() => trigger('set')}
                                        value={selectedSet}
                                        onChange={(e, event) => {
                                            setSelectedSet(event.props.value);
                                            setValue('set', event.props.value);
                                        }}
                                    >
                                        <MenuItem value={0}>None</MenuItem>
                                        {availableSets?.map((setNumber, index) => {
                                            return <MenuItem
                                                key={index}
                                                value={setNumber}
                                                disabled={assignedSets.includes(setNumber) ? true : false}
                                            >Set {setNumber}</MenuItem>
                                        })}
                                    </Select>
                                    {errors.set?.message && (
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            {errors.set.message}
                                        </FormHelperText>
                                    )}
                                </FormControl>
                                {
                                    unlockWithPassword && <FormControl variant='outlined'>
                                        <InputLabel htmlFor="passwordLabel">Password</InputLabel>
                                        <OutlinedInput
                                            id='passwordLabel'
                                            type={passwordVisible ? 'text' : 'password'}
                                            endAdornment={
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={() => setPasswordVisible(!passwordVisible)}
                                                        edge="end"
                                                    >
                                                        <img className='password-eye' src={passwordVisible ? EYE_CLOSE_ICON : EYE_ICON} alt='' />
                                                    </IconButton>
                                                </InputAdornment>
                                            }
                                            label="Password"
                                            name="password"
                                            {...register('password')}
                                            error={Boolean(errors.password)}
                                            onBlur={() => trigger('password')}
                                        />
                                        {errors?.password?.message && (
                                            <FormHelperText sx={{ color: 'error.main' }}>
                                                {errors.password?.message}
                                            </FormHelperText>
                                        )}
                                    </FormControl>
                                }

                                <LoadingButton
                                    type='submit'
                                    sx={{ height: 54, borderRadius: 40, textTransform: 'uppercase' }}
                                    loading={isEnabling}
                                    variant='contained'
                                    size='large'
                                    disabled={(!startTime || !endTime || !selectedSet) ? true : false}
                                // onClick={handleEnableExam}
                                >Enable</LoadingButton>
                            </Box>

                        </Box>
                    </Box>
                </Paper>
            </Container>
        </MainContainer >
    );
}